import React, { useEffect, useState } from "react";
import PageHeader from "../components/practice/PageHeader";
import Footer from "../components/Footer";
import SiteLoader from "../components/SiteLoader";
import SiteError from "../components/SiteError";
import { PayStepQuestion, PayStepAmount, PayStepContact, PayStepRedirect } from "../components/pay/PaySteps";
import { useContent } from "../contexts/ContentContext";
import { useSeo } from "../hooks/useSeo";

const STEP_LABELS = { question: "Step 1 of 2", amount: "Step 2 of 2", contact: "Next steps", unavailable: "Next steps", redirect: "" };
const UNAVAILABLE_MESSAGE = "Online payments are temporarily unavailable. Please contact HBHS to arrange your payment.";

function PayFlow({ firm }) {
  const [step, setStep] = useState("question");

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [step]);

  const go = (url) => {
    if (!/^https?:\/\//i.test(url || "")) return setStep("unavailable");
    setStep("redirect");
    window.location.assign(url);
  };

  const onAmount = (amount) => {
    const parsed = Number(firm.trustDepositLimit);
    const limit = Number.isFinite(parsed) ? parsed : 10000;
    if (amount > limit) setStep("contact");
    else go(firm.trustDepositUrl);
  };

  return (
    <>
      {STEP_LABELS[step] && <div className="text-[11px] uppercase tracking-[0.4em] text-[#d4b87a] mb-8" data-testid="pay-step-label">{STEP_LABELS[step]}</div>}
      {step === "question" && <PayStepQuestion onYes={() => setStep("amount")} onNo={() => go(firm.paymentUrl)} />}
      {step === "amount" && <PayStepAmount onNext={onAmount} onBack={() => setStep("question")} />}
      {step === "contact" && <PayStepContact firm={firm} />}
      {step === "unavailable" && <PayStepContact firm={firm} message={UNAVAILABLE_MESSAGE} testId="pay-step-unavailable" />}
      {step === "redirect" && <PayStepRedirect />}
    </>
  );
}

export default function PayPage() {
  const { firm, loading, error, refresh } = useContent();
  useSeo({ title: "Pay Online | Hilty, Bower, Haws & Seable, PLLC", description: "Make a secure online payment or retainer deposit to Hilty, Bower, Haws & Seable, PLLC.", path: "/pay", noindex: true });

  if (loading) return <SiteLoader />;
  if (error || !firm) return <SiteError onRetry={refresh} />;

  return (
    <div className="min-h-screen flex flex-col bg-[#0c1729]" data-testid="pay-page">
      <PageHeader />
      <main className="flex-1 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-32">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px w-12 bg-[#b8924a]" />
            <span className="text-[11px] uppercase tracking-[0.4em] text-[#d4b87a]">Online Payments</span>
          </div>
          <PayFlow firm={firm} />
        </div>
      </main>
      <Footer />
    </div>
  );
}
